import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { GridFourIcon, ListIcon, ReadCvLogoIcon } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, stripSearchParams, useNavigate, useRouter } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { getCookie, setCookie } from "@tanstack/react-start/server";
import { zodValidator } from "@tanstack/zod-adapter";
import { useMemo } from "react";
import z from "zod";

import { Combobox } from "@/components/ui/combobox";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { orpc } from "@/integrations/orpc/client";
import { cn } from "@/utils/style";

import { DashboardHeader } from "../-components/header";
import { GridView } from "./-components/grid-view";
import { ListView } from "./-components/list-view";

type SortOption = "lastUpdatedAt" | "createdAt" | "name";

const searchSchema = z.object({
  tags: z.array(z.string()).default([]),
  sort: z.enum(["lastUpdatedAt", "createdAt", "name"]).default("lastUpdatedAt"),
});

export const Route = createFileRoute("/dashboard/resumes/")({
  component: RouteComponent,
  validateSearch: zodValidator(searchSchema),
  search: {
    middlewares: [stripSearchParams({ tags: [], sort: "lastUpdatedAt" })],
  },
  loader: async () => {
    const view = await getViewServerFn();
    return { view };
  },
});

function RouteComponent() {
  const router = useRouter();
  const { i18n } = useLingui();
  const { view } = Route.useLoaderData();
  const { tags, sort } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const { data: allTags } = useQuery(orpc.resume.tags.list.queryOptions());
  const { data: resumes } = useQuery(orpc.resume.list.queryOptions({ input: { tags, sort } }));

  const tagOptions = useMemo(() => {
    if (!allTags) return [];
    return allTags.map((tag) => ({ value: tag, label: tag }));
  }, [allTags]);

  const sortOptions = useMemo(() => {
    return [
      { value: "lastUpdatedAt", label: i18n.t("Last Updated") },
      { value: "createdAt", label: i18n.t("Created") },
      { value: "name", label: i18n.t("Name") },
    ];
  }, [i18n]);

  const onViewChange = async (value: string) => {
    await setViewServerFn({ data: value as "grid" | "list" });
    void router.invalidate();
  };

  return (
    <div className="space-y-4">
      <DashboardHeader icon={ReadCvLogoIcon} title={t`Resumes`} />

      <Separator />

      <div className="flex items-center gap-x-4">
        <div className="flex gap-2">
          <Label>
            <Trans>Sort by</Trans>
          </Label>
          <Combobox
            value={sort}
            options={sortOptions}
            placeholder={t`Sort by`}
            onValueChange={(value) => {
              if (!value) return;
              void navigate({ search: { tags, sort: value as SortOption } });
            }}
          />
        </div>

        <div className={cn("flex gap-2", { hidden: tagOptions.length === 0 })}>
          <Label>
            <Trans>Filter by</Trans>
          </Label>
          <Combobox
            multiple
            value={tags}
            options={tagOptions}
            placeholder={t`Filter by`}
            onValueChange={(value) => {
              void navigate({ search: { tags: value ?? [], sort } });
            }}
          />
        </div>

        <Tabs className="ltr:ms-auto rtl:me-auto" value={view} onValueChange={onViewChange}>
          <TabsList>
            <TabsTrigger value="grid" className="rounded-r-none">
              <GridFourIcon />
              <Trans>Grid</Trans>
            </TabsTrigger>

            <TabsTrigger value="list" className="rounded-l-none">
              <ListIcon />
              <Trans>List</Trans>
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {view === "list" ? <ListView resumes={resumes ?? []} /> : <GridView resumes={resumes ?? []} />}
    </div>
  );
}

const RESUMES_VIEW_COOKIE_NAME = "resumes_view";

const viewSchema = z.enum(["grid", "list"]).catch("grid");

const setViewServerFn = createServerFn({ method: "POST" })
  .inputValidator(viewSchema)
  .handler(async ({ data }) => {
    setCookie(RESUMES_VIEW_COOKIE_NAME, JSON.stringify(data));
  });

const getViewServerFn = createServerFn({ method: "GET" }).handler(async () => {
  const view = getCookie(RESUMES_VIEW_COOKIE_NAME);
  if (!view) return "grid";
  return viewSchema.parse(JSON.parse(view));
});
